import { Controller } from "@hotwired/stimulus";

// "New Pitch" modal on the Pitches index page — lets a pitch be created
// from an existing Creator List, or from a brand-new list typed inline,
// without leaving the page. Mirrors the create-list-inline pattern from
// creator_save_list_controller.js.
export default class extends Controller {
  static targets = [
    "form",
    "listIdField",
    "existingSection",
    "existingSelect",
    "newListSection",
    "newListName",
    "toggleLink",
    "error",
    "submitButton",
  ];

  // Fomantic's dropdown module doesn't self-initialize — a bare
  // `.ui.dropdown` class on a <select> renders as an empty shell with no
  // visible selected text until `.dropdown()` runs (see pitch-editor's
  // font select for the same pattern).
  connect() {
    if (window.jQuery && this.hasExistingSelectTarget) {
      window.jQuery(this.existingSelectTarget).dropdown({
        onChange: (value) => {
          this.listIdFieldTarget.value = value;
        },
      });
    }
  }

  // Existing-list mode submits the form natively (creator_list_id is
  // already on the hidden field). New-list mode creates the list via
  // JSON first, then triggers the same native submission — which fires
  // this same "submit" action again, so `_bypassNextSubmit` lets that
  // second, programmatic submit through instead of creating the list twice.
  submit(event) {
    if (this._bypassNextSubmit) {
      this._bypassNextSubmit = false;
      return;
    }

    if (!this._creatingNewList()) return;

    event.preventDefault();
    const name = this.newListNameTarget.value.trim();
    if (!name) {
      this._showError("Enter a name for the new list.");
      return;
    }

    this._hideError();
    this.submitButtonTarget.disabled = true;
    this._createListThenSubmit(name);
  }

  toggleMode(event) {
    event.preventDefault();
    // Currently showing existing → switch to new, and vice versa.
    const showNew = this.newListSectionTarget.hidden;
    this.newListSectionTarget.hidden = !showNew;
    if (this.hasExistingSectionTarget) {
      this.existingSectionTarget.hidden = showNew;
    }
    this.toggleLinkTarget.textContent = showNew
      ? "← Use an existing list instead"
      : "+ Create a new list instead";
    this._hideError();
    if (showNew) this.newListNameTarget.focus();
  }

  _creatingNewList() {
    return (
      !this.hasExistingSectionTarget ||
      this.newListSectionTarget.hidden === false
    );
  }

  async _createListThenSubmit(name) {
    try {
      const res = await fetch("/creator_lists", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": this._csrf(),
          "X-Requested-With": "XMLHttpRequest",
        },
        credentials: "same-origin",
        body: JSON.stringify({ creator_list: { name } }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => null);
        this._showError(
          data?.errors?.join(", ") ||
            "Couldn't create that list. Try a different name."
        );
        this.submitButtonTarget.disabled = false;
        return;
      }

      const data = await res.json();
      this.listIdFieldTarget.value = data.id;
      this._bypassNextSubmit = true;
      this.formTarget.requestSubmit();
    } catch {
      this._showError("Something went wrong creating that list. Try again.");
      this.submitButtonTarget.disabled = false;
    }
  }

  _showError(message) {
    this.errorTarget.textContent = message;
    this.errorTarget.hidden = false;
  }

  _hideError() {
    this.errorTarget.hidden = true;
  }

  _csrf() {
    return document.querySelector('meta[name="csrf-token"]')?.content || "";
  }
}
